import { Component } from '@angular/core';
import { NgoService } from '../../../services/ngo/ngo.service';
import { TagCsrService } from '../../../services/tag-csr/tag-csr.service';
import { TagTypeEnum } from '../../../enum/tagCsrEnum.enum';
import { UtilitiesService } from 'src/app/shared/services/utilities/utilities.service';
import { TagCsrModel } from 'src/app/shared/model/csr/tag-csr.model';
import { LocalStorageService } from 'src/app/shared/services/local-storage/local-storage.service';
import { Router } from '@angular/router';
import { ExternalCsrCampaignDonationModel } from 'src/app/shared/model/csr/external-csr-donation.model';
import { DonationFromStatusTypeEnum } from 'src/app/shared/enum/donation-from-status-type.enum';
import { FileService } from 'src/app/shared/services/file/file.service';
import validationEngine from 'devextreme/ui/validation_engine';
import { forkJoin, switchMap } from 'rxjs';
import { CorporateCsrService } from 'src/app/shared/services/corporate/corporate-csr/corporate-csr.service';

@Component({
  selector: 'app-csr-tagged-ngos',
  templateUrl: './csr-tagged-ngos.component.html',
  styleUrl: './csr-tagged-ngos.component.scss',
})
export class CsrTaggedNgosComponent {
  public suggestedFund: any = [
    { amount: 200, isSelected: false },
    { amount: 500, isSelected: false },
    { amount: 1000, isSelected: false },
    { amount: 10000, isSelected: false },
    { amount: 20000, isSelected: false },
    { amount: 50000, isSelected: false },
  ];
  public initialSuggestedFund = [...this.suggestedFund];
  public searchKeyword = '';
  public activeTab: number = 1;

  public sessionUser = this.localStorageService.getSessionUser();
  public selectedRole = this.localStorageService.getSelectedRole();
  private companyId: string = null;

  public activeList: any[] = [];
  public ngoList: any[] = [];
  public taggedList: any[] = [];
  public tagCsrModel: TagCsrModel = new TagCsrModel();

  public externalCsrDonationCampaignModel: ExternalCsrCampaignDonationModel =
    new ExternalCsrCampaignDonationModel();

  public ngoName: string = null;
  public donationPopup = false;
  public matchedAmount: any;
  public hasMatched: boolean = false;
  public receiptName: string;
  public receiptFile: any;
  public selectedAmount: number = 10;
  public coverFees: boolean = true;
  public mapRequest: any = null;
  public openThankYouPopup: boolean = false;
  public csrMatchedNgoList: any[];
  public csrNotMatchedNgoList: any[];
  public selectedId: string;

  constructor(
    private readonly router: Router,
    private readonly ngoService: NgoService,
    private readonly fileService: FileService,
    private readonly tagCsrService: TagCsrService,
    private readonly utilitiesService: UtilitiesService,
    private readonly corporateCsrService: CorporateCsrService,
    private readonly localStorageService: LocalStorageService
  ) {}

  ngOnInit() {
    this.companyId = this.sessionUser.company_id;

    if (this.selectedRole?.short_form === 'system_admin') {
      this.getAllNgos();
      this.getAllTaggedNgos();
    } else {
      this.mapCsrListAndMatching();
    }
  }

  public getAllNgos() {
    this.ngoList = [];

    this.ngoService.getAllVerifiedNgos().subscribe({
      next: (result) => {
        this.ngoList = result.sort((a, b) => a.name.localeCompare(b.name));
      },
    });
  }

  public getAllTaggedNgos() {
    this.tagCsrService.getAllTaggedNgoCampaigns().subscribe({
      next: (result) => {
        this.taggedList = result.filter((ngo) => ngo.type === TagTypeEnum.NGO);
      },
    });
  }

  private mapCsrListAndMatching(): void {
    forkJoin([
      this.corporateCsrService.getAllCsrNgos(),
      this.corporateCsrService.getCsrNgosByCompany(this.companyId),
    ]).subscribe({
      next: ([campaignList, matchingList]: [any[], any[]]) => {
        const matchingMap = new Map(
          matchingList.map((item) => [item.ngo_id, item])
        );

        this.activeList = campaignList.map((item) => {
          const match = matchingMap.get(item.ngo_id);

          return {
            ...item,
            name: item?.ngo_name || item?.name,
            has_matching: !!match,
            ...(match
              ? {
                  matching_amount: match.matching_amount,
                  matching_percentage: match.matching_percentage,
                  matching_limit: match.matching_limit,
                  matching_budget: match.matching_budget,
                }
              : {}),
          };
        });
        this.activeList.sort((a, b) => a.ngo_name.localeCompare(b.ngo_name));
        this.csrMatchedNgoList = this.activeList.filter(
          (item) => item.has_matching
        );

        this.csrNotMatchedNgoList = this.activeList.filter(
          (item) => !item.has_matching
        );
      },
    });
  }

  public onSearchKeywordChanged(event: any) {
    this.searchKeyword = event.event.target.value.toLowerCase();
  }

  public async tagCsr(ngo: any) {
    this.tagCsrModel.ngo_id = ngo?.id;
    this.tagCsrModel.ngo_name = ngo?.name;
    this.tagCsrModel.name = ngo?.name;
    this.tagCsrModel.type = TagTypeEnum.NGO;
    this.tagCsrModel.ngo_campaign_id = ngo?.id;
    this.selectedId = ngo?.id;

    const response = await this.utilitiesService.showSwalAndReturn({
      title: 'Tag this NGO?',
      type: 'warning',
      confirmButtonText: 'Continue',
      showCancelButton: true,
      cancelButtonText: 'Cancel',
    });
    if (response?.isConfirmed) {
      this.tagCsrService.tagCampaignNgo(this.tagCsrModel).subscribe({
        next: (result: any) => {
          this.tagCsrModel = new TagCsrModel();
          ngo.is_csr_allowed = true;

          const matchedItem = this.ngoList.find(
            (item) => item.id === this.selectedId
          );

          if (matchedItem) {
            this.taggedList.push(matchedItem);
          }

          this.utilitiesService.showSwalWithToast(
            'Success',
            'NGO tagged successfully',
            'success'
          );
        },
      });
    }
  }

  public async untagCsr(campaign: any) {
    const response = await this.utilitiesService.showSwalAndReturn({
      title: 'Untag this Ngo?',
      type: 'warning',
      confirmButtonText: 'Continue',
      showCancelButton: true,
      cancelButtonText: 'Cancel',
    });
    if (response.isConfirmed) {
      this.tagCsrService.untagCsrNgoNpo(campaign?.id).subscribe({
        next: (result: any) => {
          campaign.is_csr_allowed = false;

          const matchedItemIndex = this.taggedList.findIndex(
            (item) => item.id === campaign?.id
          );

          // if (matchedItemIndex !== -1) {
          //   const [matchedItem] = this.taggedList.splice(matchedItemIndex, 1);
          // }
        },
      });
    }
  }

  public goToNgoPage(ngo: any): void {
    if (this.selectedRole?.short_form === 'system_admin') {
      this.router.navigate(['/admin/ngo-management/ngo-details'], {
        queryParams: { id: ngo?.id },
      });
    } else {
      const encryptedParams = this.localStorageService.encryptObjectUrl({
        id: ngo.elastic_id,
      });
      this.router.navigate(['/ngo'], {
        queryParams: { data: encryptedParams },
      });
    }
  }

  public donationPopupOpen(ngo: any): void {
    this.ngoName = ngo?.ngo_name;
    this.externalCsrDonationCampaignModel.campaign_title = ngo?.title;
    this.externalCsrDonationCampaignModel.campaign_id = ngo?.id;
    this.externalCsrDonationCampaignModel.ngo_id = ngo?.ngo_id;
    this.externalCsrDonationCampaignModel.name = this.sessionUser?.name;
    this.externalCsrDonationCampaignModel.email = this.sessionUser?.email;
    this.externalCsrDonationCampaignModel.is_direct_donation = false;
    this.externalCsrDonationCampaignModel.ngo_name = ngo.ngo_name;
    (this.externalCsrDonationCampaignModel.donation_from =
      DonationFromStatusTypeEnum.DONATION),
      (this.donationPopup = true);
    this.matchedAmount = ngo?.matching_percentage;
    this.hasMatched = ngo?.has_matching;
    this.receiptFile = null;
    this.receiptName = null;
  }

  public hide(): void {
    this.donationPopup = false;
    this.externalCsrDonationCampaignModel =
      new ExternalCsrCampaignDonationModel();
    validationEngine.resetGroup('validationGrp');
    this.resetSuggestedFund();
  }

  private resetSuggestedFund(): void {
    this.suggestedFund = this.initialSuggestedFund.map((item) => ({
      ...item,
      isSelected: false,
    }));
    this.selectedAmount = 10;
  }

  public selectAmount(index: number): void {
    this.suggestedFund.forEach((item) => (item.isSelected = false));
    this.suggestedFund[index].isSelected = true;
    this.selectedAmount = this.suggestedFund[index].amount;
  }

  public donate(): void {
    const { isValid } = validationEngine.validateGroup('validationGrp');

    if (isValid) {
      this.externalCsrDonationCampaignModel.pledged_amount = this.coverFees
        ? this.selectedAmount
        : this.selectedAmount - this.selectedAmount * 0.14;
      this.externalCsrDonationCampaignModel.transaction_fees =
        this.selectedAmount * 0.14;
      this.externalCsrDonationCampaignModel.company_name =
        this.localStorageService.getSessionUser().company_name;
      this.externalCsrDonationCampaignModel.company_id =
        this.localStorageService.getSessionUser().company_id;
      this.externalCsrDonationCampaignModel.user_id = this.sessionUser.id;
      this.externalCsrDonationCampaignModel.matching_request = this.mapRequest;
      this.externalCsrDonationCampaignModel.is_direct_donation = true;

      if (this.externalCsrDonationCampaignModel.matching_request === null) {
        delete this.externalCsrDonationCampaignModel.matching_request;
      }
      this.makeDonation();
    }
  }

  public makeDonation(): void {
    this.fileService
      .uploadSingleFile(this.receiptFile)
      .pipe(
        switchMap((fileResponse) => {
          this.externalCsrDonationCampaignModel.payment_receipt =
            this.utilitiesService.jsonStringify(fileResponse[0]);

          return this.tagCsrService.tagCsrNgoDonation(
            this.externalCsrDonationCampaignModel
          );
        })
      )
      .subscribe((result: any) => {
        this.externalCsrDonationCampaignModel =
          new ExternalCsrCampaignDonationModel();
        validationEngine.resetGroup('validationGrp');
        this.resetSuggestedFund();
        this.donationPopup = false;
        this.openThankYouPopup = true;
      });
  }

  public triggerFileInput(inputId: string): void {
    const input = document.getElementById(inputId) as HTMLInputElement;
    if (input) {
      input.click();
    }
  }

  public onFileChange(e: any): void {
    const input = e.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.receiptFile = input.files[0];
      this.receiptName = input.files[0].name;
    }
  }

  public onDropSuccess(event: DragEvent): void {
    event.preventDefault();
    const files = event.dataTransfer?.files;
    if (files.length > 0) {
      this.onFileChange({ event: { target: { files: files } } });
    }
  }

  public onDragOver(event: DragEvent) {
    event.preventDefault();
    const items = event.dataTransfer?.items;
    if (items) {
      event.dataTransfer.dropEffect = 'copy';
    }
  }

  public openPaymentReceiptPopup() {
    this.openThankYouPopup = false;
  }
}
